import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { Answer, RefusalCard } from '@/components/common/Answer';
import { FineDistributionPanel } from '@/pages/anatel/FineDistributionPanel';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { aneelCaseParams, fetchAneelOverview } from '@/services/aneel';
import type { AneelBodySplit, AnswerOut } from '@/services/types';

/** The case list filtered to one predicate this screen counts by. */
function casesHref(filters: Parameters<typeof aneelCaseParams>[0]): string {
  const params = aneelCaseParams(filters).toString();
  return params ? `/aneel/cases?${params}` : '/aneel/cases';
}

/**
 * ANEEL enforcement, and the one screen no other agency in this product can
 * support: what an appeal actually does to a penalty.
 *
 * ANATEL cannot answer it — its appeal linkage is far too thin to divide by,
 * and the reach is measured per request rather than asserted here — and CADE
 * cannot either. Here every resolved case carries both an
 * original and a final amount.
 *
 * The screen's whole discipline is one number: 79.5% of this corpus has not been
 * decided. Every rate is over RESOLVED cases and says so, because the same
 * numerator against all cases is five times smaller and both sentences are true.
 */
export function AneelOverviewPage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['aneel', 'overview'],
    queryFn: fetchAneelOverview,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  // Each tile opens the list it counts, and carries the nearest figure a reader
  // would mistake it for on a second line, so no number stands alone.
  const stats: {
    key: string;
    value: number;
    href?: string;
    hrefLabel?: string;
    second?: string;
  }[] = [
    {
      key: 'cases',
      value: data.corpus.cases,
      href: casesHref({}),
      hrefLabel: t('aneel:overview.corpus.openCases'),
      second: t('aneel:overview.corpus.withAmount', {
        n: number.format(data.corpus.with_original_amount),
      }),
    },
    {
      key: 'resolved',
      value: data.corpus.resolved,
      second: t('aneel:overview.corpus.unclassified', {
        n: number.format(data.corpus.unclassified),
      }),
    },
    {
      key: 'pending',
      value: data.corpus.pending,
      href: casesHref({ trajectory: 'PENDING_DECISION' }),
      hrefLabel: t('aneel:overview.corpus.openPending'),
    },
    {
      key: 'entities',
      value: data.corpus.entities,
      href: '/aneel/entities',
      hrefLabel: t('aneel:overview.corpus.openEntities'),
    },
  ];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('aneel:overview.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aneel:overview.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="aneel:overview.explainer" />

      <div className="flex justify-end">
        <ExportButton
          path="/api/v1/aneel/export/overview"
          label={t('aneel:overview.exportOverview')}
        />
      </div>

      <AsOfStrip asOf={data.as_of} />

      {/* Pending is a HEADLINE counter, not a footnote. It is four fifths of the
          corpus, and a reader who misses it will read every rate below as
          describing ANEEL enforcement rather than the decided fifth of it. */}
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {stats.map((stat) => (
          <div key={stat.key} className="rounded-lg border bg-card p-3" data-tile={stat.key}>
            <dt className="text-xs text-muted-foreground">{t(`aneel:overview.corpus.${stat.key}`)}</dt>
            <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
              {stat.href ? (
                <Link
                  to={stat.href}
                  aria-label={stat.hrefLabel}
                  className="text-primary underline-offset-2 hover:underline"
                >
                  {number.format(stat.value)}
                </Link>
              ) : (
                number.format(stat.value)
              )}
            </dd>
            {stat.second ? (
              <dd className="mt-0.5 text-xs text-muted-foreground">{stat.second}</dd>
            ) : null}
          </div>
        ))}
        {/* The appeal record's headline, from the same predicate /aneel/appeals
            reads: the two screens cannot disagree about how many rulings exist. */}
        <div className="rounded-lg border bg-card p-3" data-tile="appeals">
          <dt className="text-xs text-muted-foreground">{t('aneel:overview.appeals.title')}</dt>
          <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
            <Link
              to="/aneel/appeals"
              aria-label={t('aneel:overview.appeals.open')}
              className="text-primary underline-offset-2 hover:underline"
            >
              {number.format(data.appeals.first_minuted)}
            </Link>
          </dd>
          <dd className="mt-0.5 text-xs text-muted-foreground">
            {t('aneel:overview.appeals.board', { n: number.format(data.appeals.board_minuted) })}
          </dd>
        </div>
      </dl>

      {/* level 2 because this follows the page h1 directly: the stats grid above
          is a <dl> with no heading of its own, so an h3 here would skip a level
          and leave a keyboard reader navigating by heading with no way to tell
          why. */}
      <FieldGuide i18nKey="aneel:overview.guide.corpus" level={2} />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:overview.waterfall.title')}
        </h2>
        <Answer answer={data.reduction_waterfall} help={t('aneel:overview.waterfall.help')}>
          {(w) => (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure
                  label={t('aneel:overview.waterfall.originalTotal')}
                  value={currency.format(w.original_total)}
                />
                <Figure
                  label={t('aneel:overview.waterfall.finalTotal')}
                  value={currency.format(w.final_total)}
                />
                <Figure
                  label={t('aneel:overview.waterfall.relief')}
                  value={currency.format(w.total_relief)}
                />
                <Figure
                  label={t('aneel:overview.waterfall.medianRetained')}
                  value={w.median_retained === null ? '—' : percent.format(w.median_retained)}
                />
              </div>

              {/* Both denominators, side by side. This is the sentence the whole
                  screen exists to get right. */}
              <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                {t('aneel:overview.waterfall.bothDenominators', {
                  reduced: number.format(w.reduced),
                  resolved: number.format(w.resolved),
                  ofResolved: percent.format(w.reduced_share_of_resolved),
                  total: number.format(data.corpus.cases),
                  ofAll: percent.format(w.reduced / data.corpus.cases),
                })}
              </p>
            </div>
          )}
        </Answer>

        <div className="mt-3">
          <FieldGuide i18nKey="aneel:overview.guide.waterfall" />
        </div>
      </section>

      <FieldGuide i18nKey="aneel:overview.guide.distributions" level={2} />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* The shares are lifted out of the struct while substrate, reason, notes
            and population ride along unchanged — so the shared panel renders
            ANEEL's disclosure, not a bare list of numbers. */}
        <OutcomeMixPanel
          answer={{
            ...data.trajectory_mix,
            value: data.trajectory_mix.value?.trajectories ?? null,
          }}
          titleKey="aneel:overview.trajectoryMix.title"
          helpKey="aneel:overview.trajectoryMix.help"
          labelPrefix="aneel:overview.trajectories."
          rowHref={(code) => casesHref({ trajectory: code })}
        />
        <FineDistributionPanel
          answer={data.penalty_distribution}
          titleKey="aneel:overview.penalties.title"
          helpKey="aneel:overview.penalties.help"
          countKey="aneel:overview.penalties.count"
        />
      </div>

      <BodySplitPanel answer={data.body_split} />

      <section className="rounded-lg border bg-card p-4">
        <div className="mb-1 flex items-start justify-between gap-3">
          <h2 className="text-sm font-semibold text-foreground">
            {t('aneel:overview.natures.title')}
          </h2>
          {/* Its own button, named. The page holds two result sets in different
              shapes -- one row per figure above, one row per nature here -- and a
              single unlabelled CSV button would give a reader no way to know
              which one they downloaded. */}
          <ExportButton
            path="/api/v1/aneel/export/natures"
            label={t('aneel:overview.exportNatures')}
          />
        </div>
        <p className="mb-3 text-xs text-muted-foreground">{t('aneel:overview.natures.help')}</p>
        <div className="mb-3">
          <FieldGuide i18nKey="aneel:overview.guide.natures" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('aneel:overview.natures.nature')}</th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('aneel:overview.natures.cases')}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('aneel:overview.natures.resolved')}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('aneel:overview.natures.relief')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.natures.map((nature) => (
                <tr key={nature.nature} className="border-t" data-nature={nature.nature}>
                  <td className="px-3 py-2 text-foreground">{nature.nature}</td>
                  {/* The case count opens the list filtered to this nature:
                      the same `nature` predicate the list applies. */}
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    <Link
                      to={casesHref({ nature: nature.nature })}
                      className="text-primary underline-offset-2 hover:underline"
                      aria-label={t('aneel:overview.natures.openNature', {
                        n: number.format(nature.cases),
                        nature: nature.nature,
                      })}
                    >
                      {number.format(nature.cases)}
                    </Link>
                  </td>
                  {/* Rendered beside the case count, never instead of it. The
                      largest nature in the corpus has 670 cases and 0 decided;
                      the case count alone would present it as the most
                      analysable rather than the least. */}
                  <td
                    className={
                      nature.resolved === 0
                        ? 'px-3 py-2 text-right tabular-nums italic text-muted-foreground'
                        : 'px-3 py-2 text-right tabular-nums text-foreground'
                    }
                  >
                    {number.format(nature.resolved)}
                  </td>
                  {/* From the appeal record, a different record from the
                      trajectory: it can exceed the decided column, and the
                      guide says why. Opens the record rather than the list. */}
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    <Link
                      to="/aneel/appeals"
                      className="text-primary underline-offset-2 hover:underline"
                      title={t('aneel:overview.natures.reliefHelp')}
                      data-relief={nature.granted_any}
                    >
                      {number.format(nature.granted_any)}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* What this corpus cannot answer                                    */}
      {/* ---------------------------------------------------------------- */}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('aneel:overview.refusals.title')}
        </h2>
        <p className="text-xs text-muted-foreground">{t('aneel:overview.refusals.intro')}</p>

        {/* Cards, not omissions. Each states a measured absence a defence team
            can act on -- there is no ANEEL rapporteur to research, and no
            doctrine to cite back, and knowing that is worth a panel. */}
        <div className="grid gap-4 lg:grid-cols-3">
          <RefusalCard
            title={t('aneel:overview.refusals.decisionMakers')}
            answer={data.decision_makers}
          />
          <RefusalCard
            title={t('aneel:overview.refusals.reasoning')}
            answer={data.reasoning_layer}
          />
          <RefusalCard
            title={t('aneel:overview.refusals.precedent')}
            answer={data.precedent_graph}
          />
        </div>
      </section>
    </div>
  );
}

/**
 * Who lavrou the auto: ANEEL and the twelve state agencies it delegates
 * inspection to, alphabetically. Descriptive -- how the corpus splits -- and
 * never a comparison of bodies as adjudicators, which is the standing refusal
 * two panels below. Each row opens the list filtered to that body.
 */
function BodySplitPanel({ answer }: { answer: AnswerOut<AneelBodySplit> }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="rounded-lg border bg-card p-4" data-body-split>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('aneel:overview.bodies.title')}
      </h2>
      <Answer answer={answer} help={t('aneel:overview.bodies.help')}>
        {(split) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('aneel:overview.bodies.body')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:overview.bodies.cases')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:overview.bodies.resolved')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:overview.bodies.share')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {split.rows.map((row) => (
                    <tr key={row.body} className="border-t" data-body={row.body} data-cases={row.cases}>
                      <td className="px-3 py-2 text-foreground">{row.body}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        <Link
                          to={casesHref({ agency: row.body })}
                          className="text-primary underline-offset-2 hover:underline"
                          aria-label={t('aneel:overview.bodies.open', {
                            n: number.format(row.cases),
                            body: row.body,
                          })}
                        >
                          {number.format(row.cases)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.resolved)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {percent.format(row.resolved_share)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {split.unattributed > 0 ? (
              <p className="text-xs text-muted-foreground" data-unattributed>
                {t('aneel:overview.bodies.unattributed', { n: number.format(split.unattributed) })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
      <div className="mt-3">
        <FieldGuide i18nKey="aneel:overview.guide.bodies" />
      </div>
    </section>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums text-foreground">{value}</p>
    </div>
  );
}
